import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Spinner, Alert, Card, Button, Form, Row, Col, Badge, Modal, Container, InputGroup, ButtonGroup, Dropdown } from 'react-bootstrap';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import { usePageTitle } from '../context/PageTitleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFilter, 
  faPlus, 
  faFilePdf, 
  faDownload, 
  faCopy, 
  faTrash, 
  faEdit, 
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
  faGripVertical,
  faHashtag,
  faCalendar,
  faUser,
  faFileAlt,
  faPalette
} from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import { getCataloghi, getCatalogo, deleteCatalogo, generateCatalogoPdf, duplicateCatalogo } from '../services/api';
import ColumnOrderController from '../common/ColumnOrderController';
import Select from 'react-select';
import { debounce } from 'lodash';

const columnHelper = createColumnHelper();

// Icone per i diversi tipi di colonna
const getColumnIcon = (columnId) => {
  const iconMap = {
    id: faHashtag,
    nome: faFileAlt,
    descrizione: faFileAlt,
    template: faPalette,
    stato: faFilter,
    num_film: faHashtag,
    data_validita_inizio: faCalendar,
    data_validita_fine: faCalendar,
    pdf_path: faFilePdf,
    created_at: faCalendar
  };
  return iconMap[columnId] || faFileAlt;
};

const getColumnHeaderWithIcon = (columnId, columnName) => {
  const icon = getColumnIcon(columnId);
  return (
    <span className="d-flex align-items-center gap-1">
      <FontAwesomeIcon icon={icon} size="sm" />
      {columnName}
    </span>
  );
};

const allColumnsMap = {
    id: "ID",
    nome: "Nome",
    descrizione: "Descrizione",
    template: "Template",
    stato: "Stato",
    num_film: "Film",
    data_validita_inizio: "Valido dal",
    data_validita_fine: "Valido fino",
    pdf_path: "PDF",
    created_at: "Creato",
};

const initialVisibleColumns = ['id', 'nome', 'template', 'stato', 'num_film', 'data_validita_inizio', 'data_validita_fine', 'pdf_path', 'created_at'];
const visibilityKey = 'table-visibility-cataloghi';

// Enum stati e template — valori lowercase, allineati al DB
const STATO_CATALOGO = {
    BOZZA: 'bozza',
    PUBBLICATO: 'pubblicato',
    ARCHIVIATO: 'archiviato'
};

const TEMPLATE_CATALOGO = {
    MODERNO: 'moderno',
    VINTAGE: 'vintage',
    MINIMAL: 'minimal',
    COLORFUL: 'colorful'
};

// Advanced inline filter component
const AdvancedFilter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();
  const { filterVariant, filterOptions } = column.columnDef.meta || {};
  
  // Date columns
  const isDateColumn = ['data_validita_inizio', 'data_validita_fine', 'created_at'].includes(column.id);
  
  // Numeric columns
  const isNumericColumn = ['id', 'num_film'].includes(column.id);
  
  // Select columns
  const isSelectColumn = filterVariant === 'select';
  
  const handleClear = () => {
    column.setFilterValue(undefined);
  };
  
  // Date filter with presets
  if (isDateColumn) {
    return (
      <InputGroup size="sm">
        <Form.Control
          type="date"
          value={(typeof columnFilterValue === 'string' ? columnFilterValue : '') || ''}
          onChange={(e) => column.setFilterValue(e.target.value)}
          style={{ fontSize: '0.75rem' }}
        />
        {columnFilterValue && (
          <Button variant="outline-secondary" size="sm" onClick={handleClear} title="Rimuovi filtro">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </InputGroup>
    );
  }
  
  // Numeric range filter
  if (isNumericColumn) {
    const [min, max] = Array.isArray(columnFilterValue) ? columnFilterValue : [columnFilterValue, undefined];
    return (
      <div className="d-flex gap-1">
        <Form.Control
          type="number"
          placeholder="Min"
          value={min ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            column.setFilterValue((old) => [val, old?.[1]]);
          }}
          size="sm"
          style={{ fontSize: '0.75rem', width: '50%' }}
        />
        <Form.Control
          type="number"
          placeholder="Max"
          value={max ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            column.setFilterValue((old) => [old?.[0], val]);
          }}
          size="sm"
          style={{ fontSize: '0.75rem', width: '50%' }}
        />
        {(min || max) && (
          <Button variant="outline-secondary" size="sm" onClick={handleClear} title="Rimuovi filtro" style={{ padding: '0.25rem 0.4rem' }}>
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        )}
      </div>
    );
  }
  
  // Select filter
  if (isSelectColumn && filterOptions) {
    const isMultiSelectColumn = ['stato', 'template'].includes(column.id);
    
    if (isMultiSelectColumn) {
      // Multi-select using react-select
      const options = filterOptions.map(option => ({ value: option, label: option }));
      
      let selectedValues = [];
      if (columnFilterValue) {
        if (Array.isArray(columnFilterValue)) {
          selectedValues = columnFilterValue;
        } else if (typeof columnFilterValue === 'object' && columnFilterValue.in && Array.isArray(columnFilterValue.in)) {
          selectedValues = columnFilterValue.in;
        } else if (typeof columnFilterValue === 'string') {
          selectedValues = [columnFilterValue];
        }
      }
      
      const selectedOptions = selectedValues.map(value => ({ value, label: value }));
      
      return (
        <div style={{ minWidth: '140px' }}>
          <Select
            isMulti
            options={options}
            value={selectedOptions}
            onChange={(selected) => {
              const values = selected ? selected.map(option => option.value) : [];
              column.setFilterValue(values.length > 0 ? values : undefined);
            }}
            placeholder="Tutti"
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '28px', 
                height: '28px',
                fontSize: '0.7rem',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem',
                backgroundColor: '#ffffff',
                boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,0.25)' : 'none',
                borderColor: state.isFocused ? '#80bdff' : '#ced4da'
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '0 6px',
                height: '26px'
              }),
              input: (base) => ({
                ...base,
                margin: '0px',
                padding: '0px'
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: '26px'
              }),
              clearIndicator: (base) => ({
                ...base,
                padding: '0px 4px'
              }),
              dropdownIndicator: (base) => ({
                ...base,
                padding: '0px 4px'
              }),
              multiValue: (base) => ({
                ...base,
                margin: '1px',
                fontSize: '0.65rem'
              }),
              multiValueLabel: (base) => ({
                ...base,
                padding: '2px 4px',
                fontSize: '0.65rem'
              }),
              multiValueRemove: (base) => ({
                ...base,
                padding: '0px 2px'
              }),
              menu: (base) => ({
                ...base,
                fontSize: '0.75rem'
              }),
              option: (base) => ({
                ...base,
                padding: '6px 12px'
              })
            }}
          />
        </div>
      );
    } else {
      // Single select
      const options = filterOptions.map(option => ({ value: option, label: option }));
      const selectedOption = columnFilterValue ? { value: columnFilterValue, label: columnFilterValue } : null;
      
      return (
        <div style={{ minWidth: '120px' }}>
          <Select
            options={options}
            value={selectedOption}
            onChange={(selected) => column.setFilterValue(selected ? selected.value : undefined)}
            placeholder="Tutti"
            isClearable
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: '28px',
                height: '28px',
                fontSize: '0.7rem',
                border: '1px solid #ced4da',
                borderRadius: '0.25rem',
                backgroundColor: '#ffffff',
                boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,0.25)' : 'none',
                borderColor: state.isFocused ? '#80bdff' : '#ced4da'
              }),
              valueContainer: (base) => ({
                ...base,
                padding: '0 6px',
                height: '26px'
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: '26px'
              }),
              menu: (base) => ({
                ...base,
                fontSize: '0.75rem'
              })
            }}
          />
        </div>
      );
    }
  }
  
  // Text filter (default)
  return (
    <InputGroup size="sm">
      <Form.Control
        type="text"
        value={(columnFilterValue ?? '') || ''}
        onChange={(e) => column.setFilterValue(e.target.value)}
        placeholder={`Filtra ${column.columnDef.header()}...`}
        style={{ fontSize: '0.75rem' }}
      />
      {columnFilterValue && (
        <Button variant="outline-secondary" size="sm" onClick={handleClear} title="Rimuovi filtro">
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      )}
    </InputGroup>
  );
};

function CataloghiList() {
  usePageTitle('Cataloghi PDF');
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [cataloghi, setCataloghi] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCatalogoIds, setSelectedCatalogoIds] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Distinct values for filters
  const [distinctValues, setDistinctValues] = useState({
    stato: Object.values(STATO_CATALOGO),
    template: Object.values(TEMPLATE_CATALOGO)
  });
  
  const [allDistinctValues, setAllDistinctValues] = useState({
    stato: Object.values(STATO_CATALOGO),
    template: Object.values(TEMPLATE_CATALOGO)
  });

  // TanStack Table states
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) {
      return JSON.parse(saved);
    }
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-cataloghi');
    return saved ? JSON.parse(saved) : [{ id: 'created_at', desc: true }];
  });
  
  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-cataloghi');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Debounced version of columnFilters for API calls
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState(columnFilters);
  
  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-column-order-cataloghi');
    const defaultOrder = ['select', ...Object.keys(allColumnsMap), 'actions'];
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        parsedOrder = parsedOrder.filter(c => !['select', 'actions'].includes(c));
        return ['select', ...parsedOrder, 'actions'];
    }
    return defaultOrder;
  });
  
  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-column-sizing-cataloghi');
    return saved ? JSON.parse(saved) : {};
  });

  const [searchTerm, setSearchTerm] = useState('');

  // Debounced search term per performance
  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce((value) => {
        setSearchTerm(value);
      }, 800), // Debounce di 800ms
    []
  );

  const handleSearchChange = (e) => {
    debouncedSetSearchTerm(e.target.value);
  };

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [catalogoToDelete, setCatalogoToDelete] = useState(null);
  const [generatingPdfId, setGeneratingPdfId] = useState(null);

  // Debounce column filters to avoid too many API calls
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedColumnFilters(columnFilters);
    }, 500);
    
    handler();
    return () => handler.cancel();
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-sorting-cataloghi', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-cataloghi', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('table-column-order-cataloghi', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-column-sizing-cataloghi', JSON.stringify(columnSizing));
  }, [columnSizing]);

  useEffect(() => {
    localStorage.setItem(visibilityKey, JSON.stringify(columnVisibility));
  }, [columnVisibility]);



  const fetchCataloghi = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build filters from columnFilters and searchTerm
      const currentFilters = debouncedColumnFilters.reduce((acc, filter) => {
        const { id, value } = filter;
        
        // Multi-select filter for stato and template
        if (Array.isArray(value) && ['stato', 'template'].includes(id)) {
          if (value.length > 0) {
            acc[id] = { 'in': value };
          }
        }
        // Range filter for numbers (array with [min, max])
        else if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
          const [min, max] = value;
          const rangeFieldMap = {
            'id': 'id',
            'num_film': 'num_film'
          };
          const baseField = rangeFieldMap[id] || id;
          if (min) {
            acc[`${baseField}_min`] = parseInt(min);
          }
          if (max) {
            acc[`${baseField}_max`] = parseInt(max);
          }
        }
        // Date filters
        else if (['data_validita_inizio', 'data_validita_fine', 'created_at'].includes(id) && value) {
          acc[id] = value;
        }
        // Text filters
        else if (value !== undefined && value !== '') {
          acc[id] = value;
        }
        return acc;
      }, {});

      // Add global search if present
      if (searchTerm) {
        currentFilters._global_search = searchTerm;
      }
      
      const filters = JSON.stringify(currentFilters);
      let sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);

      // Default sorting if none set
      if (sortParams.length === 0) {
        sortParams = ['created_at:desc'];
      }

      const response = await getCataloghi(
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        null, // nomeFilter - ora gestito via columnFilters
        null, // statoFilter - ora gestito via columnFilters
        null, // templateFilter - ora gestito via columnFilters
        sortParams[0]?.split(':')[0] || 'created_at',
        sortParams[0]?.split(':')[1] || 'desc',
        filters // Aggiunto il parametro filters
      );
      
      setCataloghi(response.data.cataloghi || []);
      setTotal(response.data.total || 0);
      
      // Update distinct values if provided by backend
      if (response.data.facets) {
        setDistinctValues(response.data.facets);
        setAllDistinctValues(prev => ({
          ...prev,
          ...response.data.facets
        }));
      }
    } catch (err) {
      setError('Errore durante il caricamento dei cataloghi.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedColumnFilters, sorting, searchTerm]);

  useEffect(() => {
    fetchCataloghi();
  }, [fetchCataloghi]);

  const handleDelete = async (id) => {
    try {
      await deleteCatalogo(id);
      fetchCataloghi();
      setShowDeleteModal(false);
      setCatalogoToDelete(null);
    } catch (err) {
      setError('Errore durante l\'eliminazione del catalogo.');
      console.error(err);
    }
  };

  const handleGeneratePdf = async (id, mobileLayout = false, quality = 'high') => {
    try {
      setGeneratingPdfId(id);
      // Avvia generazione (ritorna subito con pdf_path='generating')
      await generateCatalogoPdf(id, mobileLayout, quality);
      setCataloghi(prev => prev.map(c => c.id === id ? { ...c, pdf_path: 'generating' } : c));

      // Polling ogni 5 secondi finché pdf_path != 'generating'
      const poll = async () => {
        try {
          const res = await getCatalogo(id);
          const pdfPath = res.data.pdf_path;
          if (pdfPath && pdfPath !== 'generating') {
            setCataloghi(prev => prev.map(c => c.id === id ? { ...c, pdf_path: pdfPath } : c));
            setGeneratingPdfId(null);
            const qualityText = quality === 'low' ? 'WhatsApp' : quality === 'medium' ? 'Email' : 'Stampa';
            alert(`PDF (${qualityText}) generato con successo!`);
          } else if (!pdfPath) {
            // pdf_path tornato null = errore in background
            setGeneratingPdfId(null);
            alert('Errore durante la generazione del PDF.');
          } else {
            setTimeout(poll, 5000);
          }
        } catch {
          setGeneratingPdfId(null);
        }
      };
      setTimeout(poll, 5000);
    } catch (err) {
      alert('Errore durante la generazione del PDF: ' + (err.response?.data?.detail || err.message));
      setGeneratingPdfId(null);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const catalogo = cataloghi.find(c => c.id === id);
      const nuovoNome = prompt('Nome per il catalogo duplicato:', `Copia di ${catalogo.nome}`);
      if (nuovoNome) {
        await duplicateCatalogo(id, nuovoNome);
        fetchCataloghi();
        alert('Catalogo duplicato con successo!');
      }
    } catch (err) {
      alert('Errore durante la duplicazione: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownloadPdf = (pdfPath) => {
    if (pdfPath) {
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}${pdfPath}?t=${Date.now()}`;
      window.open(url, '_blank');
    }
  };

  const getStatoBadge = (stato) => {
    const badges = {
      'bozza': <Badge bg="warning" text="dark">Bozza</Badge>,
      'pubblicato': <Badge bg="success">Pubblicato</Badge>,
      'archiviato': <Badge bg="secondary">Archiviato</Badge>
    };
    return badges[stato] || <Badge bg="secondary">{stato}</Badge>;
  };

  const getTemplateBadge = (template) => {
    const badges = {
      'moderno': <Badge bg="primary">Moderno</Badge>,
      'vintage': <Badge bg="warning" text="dark">Vintage</Badge>,
      'minimal': <Badge bg="dark">Minimal</Badge>,
      'colorful': <Badge bg="danger">Colorful</Badge>
    };
    return badges[template] || <Badge bg="info">{template}</Badge>;
  };

  const columns = useMemo(() => {
    const selectColumn = columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <Form.Check
          type="checkbox"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Form.Check
          type="checkbox"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 50,
    });

    const dataColumns = Object.keys(allColumnsMap).map((key) => {
      let cell, sortingFn, filterVariant = 'text', filterOptions = null;

      // Determinare il tipo di filtro e le opzioni per ogni colonna
      switch (key) {
        case 'stato':
          filterVariant = 'select';
          filterOptions = allDistinctValues.stato;
          break;
        case 'template':
          filterVariant = 'select';
          filterOptions = allDistinctValues.template;
          break;
        case 'id':
        case 'num_film':
          filterVariant = 'range';
          break;
        case 'data_validita_inizio':
        case 'data_validita_fine':
        case 'created_at':
          filterVariant = 'date';
          break;
        default:
          filterVariant = 'text';
      }

      switch (key) {
        case 'id':
          cell = ({ row }) => <span>{row.original.id}</span>;
          break;
        case 'nome':
          cell = ({ row }) => (
            <Link to={`/cataloghi/edit/${row.original.id}`}>
              <strong>{row.original.nome}</strong>
            </Link>
          );
          break;
        case 'descrizione':
          cell = ({ row }) => (
            <span style={{ maxWidth: '200px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.original.descrizione || '-'}
            </span>
          );
          break;
        case 'template':
          cell = ({ row }) => getTemplateBadge(row.original.template);
          break;
        case 'stato':
          cell = ({ row }) => getStatoBadge(row.original.stato);
          break;
        case 'num_film':
          cell = ({ row }) => {
            const numFilm = row.original.num_film || 0;
            return <Badge bg="info">{numFilm} film</Badge>;
          };
          sortingFn = 'alphanumeric';
          break;
        case 'data_validita_inizio':
          cell = ({ row }) => row.original.data_validita_inizio 
            ? new Date(row.original.data_validita_inizio).toLocaleDateString('it-IT')
            : '-';
          sortingFn = 'datetime';
          break;
        case 'data_validita_fine':
          cell = ({ row }) => row.original.data_validita_fine
            ? new Date(row.original.data_validita_fine).toLocaleDateString('it-IT')
            : '-';
          sortingFn = 'datetime';
          break;
        case 'pdf_path':
          cell = ({ row }) => {
            const pdfPath = row.original.pdf_path;
            if (pdfPath === 'generating' || generatingPdfId === row.original.id) {
              return <span className="text-warning"><Spinner size="sm" className="me-1" />Generazione...</span>;
            }
            if (pdfPath && pdfPath !== 'generating') {
              return (
                <Button size="sm" variant="outline-success" onClick={() => handleDownloadPdf(pdfPath)}>
                  <FontAwesomeIcon icon={faDownload} /> PDF
                </Button>
              );
            }
            return <span className="text-muted">-</span>;
          };
          break;
        case 'created_at':
          cell = ({ row }) => new Date(row.original.created_at).toLocaleDateString('it-IT');
          sortingFn = 'datetime';
          break;
        default:
          cell = ({ row }) => row.original[key] !== null && row.original[key] !== undefined
            ? String(row.original[key])
            : '-';
      }

      return columnHelper.accessor(key, {
        header: () => getColumnHeaderWithIcon(key, allColumnsMap[key]),
        cell,
        sortingFn,
        enableSorting: true,
        enableHiding: true,
        enableColumnFilter: true,
        meta: {
          filterVariant,
          filterOptions,
        },
      });
    });

    const actionsColumn = columnHelper.display({
      id: 'actions',
      header: 'Azioni',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '5px', position: 'relative' }}>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => navigate(`/cataloghi/edit/${row.original.id}`)}
            title="Modifica"
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Dropdown as={ButtonGroup} drop="down">
            <Button
              size="sm"
              variant="outline-success"
              onClick={() => handleGeneratePdf(row.original.id, false, 'high')}
              disabled={generatingPdfId === row.original.id || (row.original.num_film || 0) === 0}
              title="Genera PDF (alta qualità, per la stampa)"
            >
              {generatingPdfId === row.original.id ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FontAwesomeIcon icon={faFilePdf} />
              )}
            </Button>

            <Dropdown.Toggle 
              split 
              variant="outline-success" 
              size="sm"
              disabled={generatingPdfId === row.original.id || (row.original.num_film || 0) === 0}
              id="dropdown-split-pdf"
            />

            <Dropdown.Menu 
              style={{ 
                position: 'fixed',
                zIndex: 9999,
                backgroundColor: 'white',
                border: '1px solid #dee2e6',
                borderRadius: '0.375rem',
                boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
              }}
              popperConfig={{
                strategy: 'fixed'
              }}
            >
              <Dropdown.Header>📄 Genera PDF per</Dropdown.Header>
              <Dropdown.Item 
                onClick={() => handleGeneratePdf(row.original.id, false, 'high')}
                disabled={generatingPdfId === row.original.id}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                Alta Qualità (Stampa)
              </Dropdown.Item>
              <Dropdown.Item 
                onClick={() => handleGeneratePdf(row.original.id, false, 'medium')}
                disabled={generatingPdfId === row.original.id}
              >
                📧 Media Qualità (Email)
              </Dropdown.Item>
              <Dropdown.Item 
                onClick={() => handleGeneratePdf(row.original.id, false, 'low')}
                disabled={generatingPdfId === row.original.id}
              >
                📱 Bassa Qualità (WhatsApp)
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            size="sm"
            variant="outline-info"
            onClick={() => handleDuplicate(row.original.id)}
            title="Duplica"
          >
            <FontAwesomeIcon icon={faCopy} />
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => {
              setCatalogoToDelete(row.original);
              setShowDeleteModal(true);
            }}
            title="Elimina"
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
      size: 200,
    });

    return [selectColumn, ...dataColumns, actionsColumn];
  }, [cataloghi, navigate, generatingPdfId]);

  const table = useReactTable({
    data: cataloghi,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      columnOrder,
      columnSizing,
      rowSelection,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
    getRowId: row => row.id,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    manualFiltering: true,
  });

  // Sync selected row IDs with state for ActionToolbar
  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(key => rowSelection[key]).map(id => parseInt(id, 10));
    setSelectedCatalogoIds(selectedIds);
  }, [rowSelection]);

  const handleClearFilters = () => {
    setColumnFilters([]);
    setSearchTerm('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Funzione per renderizzare la card mobile di un catalogo
  const renderCatalogoCard = useCallback((catalogo) => {
    const isGenerating = generatingPdfId === catalogo.id;
    
    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1">
              <FontAwesomeIcon icon={faFileAlt} className="me-2 text-muted" />
              {catalogo.nome}
            </h6>
            <small className="text-muted">
              <Badge bg="info" className="me-2">{catalogo.template}</Badge>
              {catalogo.num_film} film
            </small>
          </div>
          {getStatoBadge(catalogo.stato)}
        </div>
        {catalogo.descrizione && (
          <div className="mb-2">
            <small className="text-muted">{catalogo.descrizione}</small>
          </div>
        )}
        <Row className="mb-2">
          <Col xs={6}>
            <small className="text-muted d-block">Valido dal</small>
            <strong>{formatDate(catalogo.data_validita_inizio)}</strong>
          </Col>
          <Col xs={6}>
            <small className="text-muted d-block">Valido fino</small>
            <strong>{formatDate(catalogo.data_validita_fine)}</strong>
          </Col>
        </Row>
        <div className="d-flex gap-2 mt-3 flex-wrap">
          <Button
            as={Link}
            to={`/cataloghi/${catalogo.id}`}
            variant="warning"
            size="sm"
            className="flex-fill"
          >
            <FontAwesomeIcon icon={faEdit} className="me-1" />
            Modifica
          </Button>
          <Button
            variant="primary"
            size="sm"
            className="flex-fill"
            onClick={() => handleGeneratePdf(catalogo.id, false, 'high')}
            disabled={isGenerating}
          >
            <FontAwesomeIcon icon={faFilePdf} className="me-1" />
            {isGenerating ? 'Generazione...' : 'PDF'}
          </Button>
          <Button
            variant="info"
            size="sm"
            onClick={() => handleDuplicate(catalogo.id)}
          >
            <FontAwesomeIcon icon={faCopy} />
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setCatalogoToDelete(catalogo);
              setShowDeleteModal(true);
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </div>
    );
  }, [generatingPdfId]);

  if (loading && cataloghi.length === 0) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p>Caricamento cataloghi...</p>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {/* Toolbar Avanzato */}
      <Row className="mb-3">
        <Col md={3} className="d-flex align-items-center">
          <Link to="/cataloghi/new">
            <Button variant="success">
              <FontAwesomeIcon icon={faPlus} /> Nuovo Catalogo
            </Button>
          </Link>
        </Col>
        
        <Col md={5} className="d-flex justify-content-center align-items-center">
          <InputGroup style={{ maxWidth: '320px' }}>
            <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
            <Form.Control
              placeholder="Ricerca globale..."
              aria-label="Ricerca globale"
              onChange={handleSearchChange}
              size="sm"
            />
          </InputGroup>
        </Col>
        
        <Col md={4} className="d-flex justify-content-end align-items-center gap-2">
          <Dropdown>
            <Dropdown.Toggle variant="outline-secondary" size="sm" title="Gestione colonne">
              <FontAwesomeIcon icon={faGripVertical} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Header>Visibilità Colonne</Dropdown.Header>
              {Object.entries(allColumnsMap).map(([key, label]) => (
                <Dropdown.Item key={key} as="div" onClick={(e) => e.preventDefault()}>
                  <Form.Check 
                    type="checkbox"
                    id={`column-${key}`}
                    label={label}
                    checked={columnVisibility[key] !== false}
                    onChange={(e) => {
                      const newVisibility = { ...columnVisibility, [key]: e.target.checked };
                      setColumnVisibility(newVisibility);
                      localStorage.setItem(visibilityKey, JSON.stringify(newVisibility));
                    }}
                  />
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleClearFilters}
            title="Pulisci tutti i filtri"
          >
            <FontAwesomeIcon icon={faTimes} /> Pulisci filtri
          </Button>
        </Col>
      </Row>

      {/* Tabella */}
      <Card>
        <Card.Body>
          {isMobile ? (
            <MobileListLayout
              items={cataloghi}
              renderCard={renderCatalogoCard}
              entityName="catalogo"
              entityNamePlural="cataloghi"
              createPath="/cataloghi/new"
              createButtonText="Nuovo Catalogo"
              showSelectAll={false}
            />
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <Table striped bordered hover responsive>
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <th
                          key={header.id}
                          style={{
                            width: header.getSize(),
                            position: 'relative',
                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? null}
                          {header.column.getCanResize() && (
                            <div
                              onMouseDown={header.getResizeHandler()}
                              onTouchStart={header.getResizeHandler()}
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                height: '100%',
                                width: '5px',
                                cursor: 'col-resize',
                                userSelect: 'none',
                                touchAction: 'none',
                                backgroundColor: header.column.getIsResizing() ? 'blue' : 'transparent',
                              }}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                    <tr key={`${headerGroup.id}-filters`}>
                      {headerGroup.headers.map(header => {
                        const isSelectColumn = header.column.id === 'select';
                        const isActionsColumn = header.column.id === 'actions';
                        
                        return (
                          <th key={`${header.id}-filter`} style={{ backgroundColor: '#f8f9fa', padding: '0.5rem' }}>
                            {header.column.getCanFilter() && !isSelectColumn && !isActionsColumn ? (
                              <AdvancedFilter column={header.column} />
                            ) : null}
                          </th>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="text-center">
                      Nessun catalogo trovato.
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} style={{ width: cell.column.getSize() }}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          )}

          {/* Paginazione */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Mostrando {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, total)} di {total} cataloghi
            </div>
            <div>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Precedente
              </Button>
              <span className="mx-2">Pagina {currentPage} di {Math.ceil(total / itemsPerPage)}</span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage * itemsPerPage >= total}
              >
                Successiva
              </Button>
            </div>
            <Form.Group style={{ width: '100px' }}>
              <Form.Select value={itemsPerPage} onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      {/* Modal Conferma Eliminazione */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sei sicuro di voler eliminare il catalogo <strong>{catalogoToDelete?.nome}</strong>?
          Questa azione non può essere annullata.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={() => handleDelete(catalogoToDelete.id)}>
            Elimina
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CataloghiList;
